import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import MarkdownEditor from "@/components/MarkdownEditor";
import {
  FileText, FloppyDisk, UploadSimple, EyeSlash, ClockCounterClockwise, Plus,
  ArrowCounterClockwise, ArrowSquareOut, Trash,
} from "@phosphor-icons/react";

const CONTENT_TYPE = "legal";

function StatusBadge({ status }) {
  const published = status === "published";
  return (
    <Badge
      variant={published ? "default" : "secondary"}
      data-testid="page-status-badge"
      className="rounded-sm"
    >
      {published ? "Published" : "Draft"}
    </Badge>
  );
}

export default function LegalCMS() {
  const [pages, setPages] = useState([]);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newPage, setNewPage] = useState({ title: "", slug: "" });

  const loadPages = useCallback(async (keepSel) => {
    try {
      const { data } = await api.get("/content/pages", { params: { content_type: CONTENT_TYPE } });
      setPages(data);
      const target = keepSel || selId || (data[0] && data[0].id);
      if (target) await selectPage(target);
    } catch (e) {
      toast.error(apiError(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  const selectPage = async (id) => {
    try {
      const { data } = await api.get(`/content/pages/${id}`);
      setSelId(id);
      setDraft(data);
      setDirty(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchDraft = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/content/pages/${draft.id}`, {
        title: draft.title, slug: draft.slug, body: draft.body, meta: draft.meta || {},
      });
      setDraft(data);
      setDirty(false);
      setPages((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      toast.success("Draft saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!draft) return;
    if (dirty) await save();
    try {
      const { data } = await api.post(`/content/pages/${draft.id}/publish`);
      setDraft(data);
      setPages((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      toast.success(`Published v${data.version}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unpublish = async () => {
    if (!draft) return;
    try {
      const { data } = await api.post(`/content/pages/${draft.id}/unpublish`);
      setDraft(data);
      setPages((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      toast.success("Unpublished");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openHistory = async () => {
    try {
      const { data } = await api.get(`/content/pages/${draft.id}/versions`);
      setVersions(data);
      setHistOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const restore = async (version) => {
    try {
      const { data } = await api.post(`/content/pages/${draft.id}/restore/${version}`);
      setDraft(data);
      setDirty(false);
      setHistOpen(false);
      setPages((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      toast.success(`Restored v${version} into draft`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createPage = async () => {
    if (!newPage.title.trim() || !newPage.slug.trim()) return;
    try {
      const { data } = await api.post("/content/pages", {
        content_type: CONTENT_TYPE, title: newPage.title.trim(),
        slug: newPage.slug.trim().toLowerCase().replace(/\s+/g, "-"), body: `# ${newPage.title.trim()}\n\n`,
      });
      setNewOpen(false);
      setNewPage({ title: "", slug: "" });
      await loadPages(data.id);
      toast.success("Page created");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removePage = async () => {
    if (!draft || !window.confirm(`Delete "${draft.title}"? This removes all versions.`)) return;
    try {
      await api.delete(`/content/pages/${draft.id}`);
      setDraft(null);
      setSelId(null);
      const { data } = await api.get("/content/pages", { params: { content_type: CONTENT_TYPE } });
      setPages(data);
      if (data[0]) await selectPage(data[0].id);
      toast.success("Page deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="legal-cms">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText size={28} weight="bold" /> Legal Pages
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Database-driven legal & content pages. Public pages render the latest published version.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="rounded-sm gap-2" data-testid="new-page-button">
          <Plus size={16} weight="bold" /> New Page
        </Button>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Page list */}
        <aside className="space-y-1" data-testid="cms-page-list">
          {pages.map((p) => (
            <button
              key={p.id}
              onClick={() => selectPage(p.id)}
              data-testid={`cms-page-${p.slug}`}
              className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors flex items-center justify-between gap-2 ${
                selId === p.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >
              <span className="truncate">{p.title}</span>
              <span className={`text-[10px] font-mono ${selId === p.id ? "opacity-80" : "text-muted-foreground"}`}>
                {p.status === "published" ? `v${p.version}` : "draft"}
              </span>
            </button>
          ))}
        </aside>

        {/* Editor */}
        {draft ? (
          <section className="space-y-4" data-testid="cms-editor-panel">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={draft.status} />
              <span className="text-xs text-muted-foreground font-mono">
                /{draft.slug} · v{draft.version} · updated by {draft.updated_by || "—"}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={openHistory} data-testid="history-button">
                  <ClockCounterClockwise size={15} /> History
                </Button>
                <a href={`/${draft.slug}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="preview-button">
                    <ArrowSquareOut size={15} /> Preview
                  </Button>
                </a>
                {draft.status === "published" ? (
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={unpublish} data-testid="unpublish-button">
                    <EyeSlash size={15} /> Unpublish
                  </Button>
                ) : null}
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-destructive" onClick={removePage} data-testid="delete-button">
                  <Trash size={15} />
                </Button>
                <Button variant="secondary" size="sm" className="rounded-sm gap-1.5" onClick={save} disabled={saving || !dirty} data-testid="save-button">
                  <FloppyDisk size={15} /> {saving ? "Saving…" : "Save Draft"}
                </Button>
                <Button size="sm" className="rounded-sm gap-1.5" onClick={publish} data-testid="publish-button">
                  <UploadSimple size={15} /> Publish
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-[1fr_220px] gap-3">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={draft.title} onChange={(e) => patchDraft({ title: e.target.value })} data-testid="page-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Slug</Label>
                <Input value={draft.slug} onChange={(e) => patchDraft({ slug: e.target.value })} data-testid="page-slug-input" className="font-mono" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Content (Markdown)</Label>
              <MarkdownEditor value={draft.body || ""} onChange={(v) => patchDraft({ body: v })} />
            </div>
          </section>
        ) : (
          <div className="text-muted-foreground text-sm" data-testid="cms-empty">Select a page to edit.</div>
        )}
      </div>

      {/* Version history dialog */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-lg" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Version History</DialogTitle>
            <DialogDescription>Restore a previous version into the working draft.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-auto space-y-2">
            {versions.length === 0 && <p className="text-sm text-muted-foreground">No published versions yet.</p>}
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`version-row-${v.version}`}>
                <div className="text-sm">
                  <span className="font-mono font-semibold">v{v.version}</span>
                  <span className="text-muted-foreground"> · {v.title}</span>
                  <div className="text-xs text-muted-foreground font-mono">
                    {new Date(v.created_at).toLocaleString()} · {v.created_by || "—"}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => restore(v.version)} data-testid={`restore-${v.version}`}>
                  <ArrowCounterClockwise size={14} /> Restore
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* New page dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-page-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Legal Page</DialogTitle>
            <DialogDescription>Create a new content page (draft).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={newPage.title} onChange={(e) => setNewPage((n) => ({ ...n, title: e.target.value }))} data-testid="new-page-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input value={newPage.slug} onChange={(e) => setNewPage((n) => ({ ...n, slug: e.target.value }))} className="font-mono" data-testid="new-page-slug" placeholder="e.g. refund-policy" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createPage} className="rounded-sm" data-testid="create-page-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
